import chunk from './chunk';

type PendingLoad<Value> = {
    resolve: (value: Value | undefined) => void;
    reject: (error: unknown) => void;
};

export type BatchLoader<Key, Value> = {
    load: (key: Key) => Promise<Value | undefined>;
};

// Collects every key requested within one macrotask (e.g. all cards mounted in one render) and
// resolves them with as few loadMany calls as maxBatchSize allows, instead of one request per key.
const createBatchLoader = <Key, Value>(
    loadMany: (keys: Key[]) => Promise<Map<Key, Value>>,
    maxBatchSize: number
): BatchLoader<Key, Value> => {
    let pendingLoads = new Map<Key, PendingLoad<Value>[]>();

    const settleBatch = (keys: Key[], loads: Map<Key, PendingLoad<Value>[]>) =>
        loadMany(keys).then(
            (values) => keys.forEach((key) => loads.get(key)?.forEach(({ resolve }) => resolve(values.get(key)))),
            (error) => keys.forEach((key) => loads.get(key)?.forEach(({ reject }) => reject(error)))
        );

    const flushPendingLoads = () => {
        const loads = pendingLoads;

        pendingLoads = new Map();
        chunk(Array.from(loads.keys()), maxBatchSize).forEach((keys) => settleBatch(keys, loads));
    };

    const load = (key: Key) =>
        new Promise<Value | undefined>((resolve, reject) => {
            if (!pendingLoads.size) {
                setTimeout(flushPendingLoads, 0);
            }

            pendingLoads.set(key, [...(pendingLoads.get(key) ?? []), { resolve, reject }]);
        });

    return { load };
};

export default createBatchLoader;
